import React, { useContext, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, Collapse, IconButton, Typography, Tooltip } from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SidebarContext } from '../../context/SidebarContext';
import { authContext } from '../../context/AuthContext';
import { tokens } from '../../theme';
import Bread_Bskt from '../../images/Bread_Bskt.png';

const WIDTH_EXPANDED = 248;
const WIDTH_COLLAPSED = 76;

function NavItem({ to, icon, label, collapsed, end = false, children }) {
  const location = useLocation();
  const active = end
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const content = (
    <Box
      component={NavLink}
      to={to}
      end={end}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: collapsed ? 1.25 : 1.5,
        py: 1.1,
        mx: 1,
        mb: 0.35,
        borderRadius: 2,
        textDecoration: 'none',
        color: active ? tokens.accentHover : tokens.inkMuted,
        backgroundColor: active ? 'rgba(91, 127, 94, 0.14)' : 'transparent',
        fontWeight: active ? 650 : 500,
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
          backgroundColor: active ? 'rgba(91, 127, 94, 0.18)' : 'rgba(44, 36, 27, 0.05)',
          color: tokens.ink,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', color: active ? tokens.accent : 'inherit' }}>
        {icon}
      </Box>
      {!collapsed && (
        <Typography variant="body2" sx={{ fontWeight: 'inherit', letterSpacing: 0.1 }}>
          {label}
        </Typography>
      )}
    </Box>
  );

  if (collapsed) {
    return (
      <Tooltip title={label} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }

  return (
    <>
      {content}
      {children}
    </>
  );
}

function SubItem({ to, label, collapsed, end = false }) {
  if (collapsed) return null;
  return (
    <Box
      component={NavLink}
      to={to}
      end={end}
      sx={{
        display: 'block',
        ml: 5.5,
        mr: 1.5,
        mb: 0.35,
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        textDecoration: 'none',
        color: tokens.inkMuted,
        fontSize: 13,
        fontWeight: 500,
        '&.active, &:hover': {
          color: tokens.ink,
          backgroundColor: 'rgba(44, 36, 27, 0.04)',
        },
        '&.active': {
          fontWeight: 650,
          color: tokens.accentHover,
          backgroundColor: 'rgba(91, 127, 94, 0.10)',
        },
      }}
    >
      {label}
    </Box>
  );
}

function NavGroup({ label, icon, to, collapsed, open, onToggle, children, paths = [] }) {
  const location = useLocation();
  const childActive = paths.some((p) => location.pathname.startsWith(p));

  if (collapsed) {
    return <NavItem to={to} icon={icon} label={label} collapsed />;
  }

  return (
    <Box sx={{ mb: 0.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mx: 1,
          mb: 0.35,
          borderRadius: 2,
          backgroundColor: childActive ? 'rgba(91, 127, 94, 0.10)' : 'transparent',
        }}
      >
        <Box
          component={NavLink}
          to={to}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1.1,
            textDecoration: 'none',
            color: childActive ? tokens.accentHover : tokens.inkMuted,
            fontWeight: childActive ? 650 : 500,
            '&:hover': { color: tokens.ink },
          }}
        >
          <Box sx={{ display: 'flex', color: childActive ? tokens.accent : 'inherit' }}>{icon}</Box>
          <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>{label}</Typography>
        </Box>
        <IconButton size="small" onClick={onToggle} sx={{ mr: 0.5, color: tokens.inkMuted }}>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 160ms ease',
            }}
          />
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </Box>
  );
}

export default function AppSidebar({ isMobile }) {
  const { isOpen, toggleSidebar } = useContext(SidebarContext);
  const { userRole } = useContext(authContext);
  const location = useLocation();
  const collapsed = !isOpen && !isMobile;
  const hideOnMobile = isMobile && !isOpen;
  const width = hideOnMobile ? 0 : ((isMobile || isOpen) ? WIDTH_EXPANDED : WIDTH_COLLAPSED);

  const [insightsOpen, setInsightsOpen] = React.useState(
    location.pathname.startsWith('/orders-log') || location.pathname.startsWith('/analytics')
  );
  const [inventoryOpen, setInventoryOpen] = React.useState(
    location.pathname.startsWith('/inventory') || location.pathname.startsWith('/item-sku') || location.pathname.startsWith('/shelving') || location.pathname.startsWith('/batches')
  );

  React.useEffect(() => {
    if (location.pathname.startsWith('/orders-log') || location.pathname.startsWith('/analytics')) {
      setInsightsOpen(true);
    }
    if (location.pathname.startsWith('/inventory') || location.pathname.startsWith('/item-sku') || location.pathname.startsWith('/shelving') || location.pathname.startsWith('/batches')) {
      setInventoryOpen(true);
    }
  }, [location.pathname]);

  const shellSx = useMemo(() => ({
    width,
    minWidth: width,
    height: '100%',
    display: hideOnMobile ? 'none' : 'flex',
    flexDirection: 'column',
    background: `linear-gradient(180deg, #EEF3EC 0%, ${tokens.bg} 42%, #E8EEE4 100%)`,
    borderRight: `1px solid ${tokens.border}`,
    transition: 'width 200ms ease, min-width 200ms ease',
    position: isMobile ? 'fixed' : 'relative',
    zIndex: isMobile ? 1200 : 20,
    left: 0,
    top: 0,
    overflow: 'hidden',
  }), [width, isMobile, hideOnMobile]);

  if (hideOnMobile) {
    return null;
  }

  const sidebar = (
    <Box component="aside" className="app-sidebar" sx={shellSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 1 : 2,
          pt: 2.25,
          pb: 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box
            component="img"
            src={Bread_Bskt}
            alt="Bread Bskt"
            sx={{ width: 36, height: 36, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
          />
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "'Fraunces', 'Figtree', serif",
                  fontWeight: 650,
                  fontSize: 17,
                  lineHeight: 1.15,
                  color: tokens.ink,
                  letterSpacing: '-0.02em',
                }}
              >
                Bread Bskt
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.inkMuted, display: 'block', mt: 0.15 }}>
                Inventory
              </Typography>
            </Box>
          )}
        </Box>
        {!collapsed && (
          <IconButton
            size="small"
            onClick={toggleSidebar}
            aria-label="Collapse sidebar"
            sx={{
              color: tokens.inkMuted,
              border: `1px solid ${tokens.border}`,
              backgroundColor: 'rgba(255,254,251,0.65)',
              '&:hover': { backgroundColor: tokens.surface, color: tokens.ink },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {collapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <IconButton
            size="small"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
            sx={{
              color: tokens.inkMuted,
              border: `1px solid ${tokens.border}`,
              backgroundColor: 'rgba(255,254,251,0.65)',
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ px: collapsed ? 0.5 : 0.75, mb: 1 }}>
        {!collapsed && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              px: 2,
              mb: 1,
              color: tokens.brown,
              fontWeight: 650,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: 10,
            }}
          >
            Workspace
          </Typography>
        )}

        <NavGroup
          label="Insights"
          icon={<AssessmentOutlinedIcon fontSize="small" />}
          to="/analytics"
          collapsed={collapsed}
          open={insightsOpen}
          onToggle={() => setInsightsOpen((v) => !v)}
          paths={['/analytics', '/orders-log']}
        >
          <SubItem to="/analytics" label="Overview" collapsed={collapsed} />
          <SubItem to="/orders-log" label="Orders log" collapsed={collapsed} />
        </NavGroup>

        <NavGroup
          label="Inventory"
          icon={<Inventory2OutlinedIcon fontSize="small" />}
          to="/inventory"
          collapsed={collapsed}
          open={inventoryOpen}
          onToggle={() => setInventoryOpen((v) => !v)}
          paths={['/inventory', '/item-sku', '/batches', '/shelving']}
        >
          <SubItem to="/inventory" label="Products" collapsed={collapsed} end />
          <SubItem to="/inventory/raw-ingredients" label="Raw ingredients" collapsed={collapsed} />
          <SubItem to="/inventory/supplies" label="Daily use supplies" collapsed={collapsed} />
          <SubItem to="/item-sku" label="Item SKUs" collapsed={collapsed} />
          <SubItem to="/batches" label="Batches" collapsed={collapsed} />
          <SubItem to="/shelving" label="Shelving" collapsed={collapsed} />
        </NavGroup>

        <NavItem
          to="/invoices"
          icon={<ReceiptLongOutlinedIcon fontSize="small" />}
          label="Invoices"
          collapsed={collapsed}
        />
        <NavItem
          to="/vendors"
          icon={<LocalShippingOutlinedIcon fontSize="small" />}
          label="Vendors"
          collapsed={collapsed}
        />

        {userRole === 'AppAdmin' && (
          <>
            {!collapsed && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  mt: 2.5,
                  mb: 1,
                  color: tokens.brown,
                  fontWeight: 650,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                }}
              >
                Admin
              </Typography>
            )}
            <NavItem
              to="/app-administration"
              icon={<SettingsOutlinedIcon fontSize="small" />}
              label="App admin"
              collapsed={collapsed}
            />
          </>
        )}
      </Box>

      <Box sx={{ flex: 1 }} />

      {!collapsed && (
        <Box sx={{ px: 2.25, pb: 2.5 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              backgroundColor: 'rgba(255,254,251,0.55)',
              border: `1px solid ${tokens.border}`,
            }}
          >
            <Typography variant="caption" sx={{ color: tokens.inkMuted, display: 'block', lineHeight: 1.45 }}>
              Track raw stock, recipes, and live COGs in one place.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {isMobile && isOpen && (
        <Box
          onClick={toggleSidebar}
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(44, 36, 27, 0.28)',
            zIndex: 1100,
          }}
        />
      )}
      {sidebar}
    </>
  );
}

export { WIDTH_EXPANDED, WIDTH_COLLAPSED };
